import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useSEO } from "@/lib/useSEO";
import { PHONE_NUMBER, PHONE_DISPLAY, WHATSAPP_NUMBER, WA_DISPLAY } from "@/lib/cart";
import { usePolicyContent } from "@/lib/firestoreService";
import { renderPolicyContent } from "@/lib/policyMarkdown";

const PolicyLayout = ({ title, children }) => {
  useSEO({ title, description: `${title} for Annapurna Home Pickles (ahpickles.in).` });
  return (
    <section className="py-10 sm:py-14 bg-[#F5E6CC] min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#5A2D1D] font-semibold hover:underline mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#5A2D1D] mb-8">{title}</h1>
        <div className="text-sm text-[#2E1A12]/90 leading-relaxed space-y-4">
          {children}
        </div>
      </div>
    </section>
  );
};

const H2 = ({ children }) => (
  <h2 className="font-display text-xl font-bold text-[#5A2D1D] mt-8 mb-1">{children}</h2>
);

const OL = ({ children }) => (
  <ol className="list-decimal pl-5 space-y-3">{children}</ol>
);

const BUSINESS_ADDRESS = (
  <>Annapurna Home Pickles, 2-283/3, Kadiyapusavaram, Kadiam, Rajahmundry Rural, East Godavari District, Andhra Pradesh – 533101, India</>
);

export const TermsPage = () => {
  const policyContent = usePolicyContent();
  if (policyContent.terms?.trim()) {
    return <PolicyLayout title="Terms & Conditions">{renderPolicyContent(policyContent.terms)}</PolicyLayout>;
  }
  return (
  <PolicyLayout title="Terms & Conditions">
    <p>
      This document is an electronic record in terms of the Information Technology Act, 2000 and rules
      made thereunder, as applicable, and the amended provisions pertaining to electronic records in
      various statutes as amended by the Information Technology Act, 2000. This electronic record is
      generated by a computer system and does not require any physical or digital signatures.
    </p>
    <p>
      This document is published in accordance with the provisions of Rule 3 (1) of the Information
      Technology (Intermediaries Guidelines) Rules, 2011, which require publishing the rules and
      regulations, privacy policy, and Terms of Use for access or usage of the domain{" "}
      <a href="https://www.ahpickles.in/" className="text-[#556B2F] font-semibold hover:underline">https://www.ahpickles.in/</a>{" "}
      ("Website"), including any related mobile site and mobile application (collectively, the "Platform").
    </p>
    <p>
      The Platform is owned and operated by <strong>Annapurna Home Pickles</strong>, with its registered
      office at <strong>{BUSINESS_ADDRESS}</strong> (hereinafter referred to as the "Platform Owner", "we", "us", "our").
    </p>
    <p>
      Your use of the Platform and our services and tools is governed by the following terms and
      conditions ("Terms of Use") as applicable to the Platform, including the applicable policies which
      are incorporated herein by way of reference. If You transact on the Platform, You shall be subject to
      the policies applicable to the Platform for such transaction. By mere use of the Platform, You shall be
      contracting with the Platform Owner, and these terms and conditions, including the policies,
      constitute Your binding obligations with the Platform Owner. These Terms of Use relate to your use
      of our website and services (collectively, "Services"). Any terms and conditions proposed by You
      which are in addition to or which conflict with these Terms of Use are expressly rejected by the
      Platform Owner and shall be of no force or effect. These Terms of Use can be modified at any time
      without assigning any reason. It is your responsibility to periodically review these Terms of Use to
      stay informed of updates.
    </p>
    <p>
      For the purpose of these Terms of Use, wherever the context so requires, "you", "your" or "user"
      shall mean any natural or legal person who has agreed to become a user/buyer on the Platform.
    </p>
    <p className="font-semibold">
      ACCESSING, BROWSING, OR OTHERWISE USING THE PLATFORM INDICATES YOUR AGREEMENT TO
      ALL THE TERMS AND CONDITIONS UNDER THESE TERMS OF USE, SO PLEASE READ THE TERMS
      OF USE CAREFULLY BEFORE PROCEEDING.
    </p>

    <H2>Terms of Use</H2>
    <p>The use of the Platform and/or availing of our Services is subject to the following Terms of Use:</p>
    <OL>
      <li>To access and use the Services, you agree to provide true, accurate, and complete information to us during and after registration, and you shall be responsible for all acts done through the use of your registered account on the Platform.</li>
      <li>Neither we nor any third parties provide any warranty or guarantee as to the accuracy, timeliness, performance, completeness, or suitability of the information and materials offered on this website or through the Services for any specific purpose. You acknowledge that such information and materials may contain inaccuracies or errors, and we expressly exclude liability for any such inaccuracies or errors to the fullest extent permitted by law.</li>
      <li>Your use of our Services and the Platform is solely and entirely at your own risk and discretion, for which we shall not be liable to you in any manner. You are required to independently assess and ensure that the Services meet your requirements.</li>
      <li>The contents of the Platform and the Services are proprietary to us and are licensed to us. You will not have any authority to claim any intellectual property rights, title, or interest in its contents, including but not limited to the design, layout, look, and graphics.</li>
      <li>You acknowledge that unauthorized use of the Platform and/or the Services may lead to action against you as per these Terms of Use and/or applicable laws.</li>
      <li>You agree to pay us the charges associated with availing the Services.</li>
      <li>You agree not to use the Platform and/or Services for any purpose that is unlawful, illegal, or forbidden by these Terms, or by Indian or local laws that might apply to you.</li>
      <li>You agree and acknowledge that the website and Services may contain links to other third-party websites. On accessing these links, you will be governed by the terms of use, privacy policy, and other policies of such third-party websites. These links are provided for your convenience to provide further information.</li>
      <li>You understand that upon initiating a transaction for availing the Services, you are entering into a legally binding and enforceable contract with the Platform Owner for the Services.</li>
      <li>You shall indemnify and hold harmless the Platform Owner, its affiliates, group companies (as applicable), and their respective officers, directors, agents, and employees from any claim or demand, or actions including reasonable attorney's fees, made by any third party or penalty imposed due to or arising out of Your breach of these Terms of Use, the Privacy Policy, and other Policies, or Your violation of any law, rules, or regulations, or the rights (including infringement of intellectual property rights) of a third party.</li>
      <li>Notwithstanding anything contained in these Terms of Use, the parties shall not be liable for any failure to perform an obligation under these Terms if performance is prevented or delayed by a force majeure event.</li>
      <li>These Terms and any dispute or claim relating to them, or their enforceability, shall be governed by and construed in accordance with the laws of India.</li>
      <li>All disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts in Rajahmundry, Andhra Pradesh.</li>
      <li>All concerns or communications relating to these Terms must be communicated to us using the contact information below.</li>
    </OL>

    <H2>Contact Us</H2>
    <p>
      {BUSINESS_ADDRESS}<br />
      Phone: <a href={`tel:+${PHONE_NUMBER}`} className="text-[#556B2F] font-semibold hover:underline">{PHONE_DISPLAY}</a><br />
      Email: <a href="mailto:annapurnahomepickles@zohomail.in" className="text-[#556B2F] font-semibold hover:underline">annapurnahomepickles@zohomail.in</a>
    </p>
  </PolicyLayout>
  );
};

export const PrivacyPage = () => {
  const policyContent = usePolicyContent();
  if (policyContent.privacy?.trim()) {
    return <PolicyLayout title="Privacy Policy">{renderPolicyContent(policyContent.privacy)}</PolicyLayout>;
  }
  return (
  <PolicyLayout title="Privacy Policy">
    <H2>Introduction</H2>
    <p>
      This Privacy Policy describes how <strong>Annapurna Home Pickles</strong> ("we", "our", "us") collects,
      uses, shares, protects, or otherwise processes your information/personal data through our website{" "}
      <a href="https://www.ahpickles.in/" className="text-[#556B2F] font-semibold hover:underline">https://www.ahpickles.in/</a>{" "}
      (the "Platform"). You may browse certain sections of the Platform without registering with us. We do
      not offer any product/service under this Platform outside India, and your personal data will
      primarily be stored and processed in India. By visiting this Platform, providing your information, or
      availing any product/service offered on the Platform, you expressly agree to be bound by the terms
      and conditions of this Privacy Policy, the Terms of Use, and the applicable service/product terms and
      conditions, and agree to be governed by the laws of India, including but not limited to the laws
      applicable to data protection and privacy. If you do not agree, please do not use or access our Platform.
    </p>

    <H2>Collection</H2>
    <p>
      We collect your personal data when you use our Platform, our services, or otherwise interact with us
      during the course of our relationship, and related information provided from time to time. Some of
      the information we may collect includes, but is not limited to, personal data provided to us during
      sign-up/registration or while using our Platform, such as name, date of birth, address,
      telephone/mobile number, email ID, and/or any such information shared as proof of identity or
      address. Some sensitive personal data may be collected with your consent, such as your bank account,
      credit/debit card, or other payment instrument information, in accordance with applicable law. You
      always have the option not to provide information by choosing not to use a particular service or
      feature on the Platform. We may track your behaviour, preferences, and other information you choose
      to provide on our Platform, compiled and analysed on an aggregated basis. If you receive an email or
      a call from a person/association claiming to be Annapurna Home Pickles and seeking personal data
      such as your debit/credit card PIN or net-banking/mobile banking password, please do not provide
      such information and report it to an appropriate law enforcement agency.
    </p>

    <H2>Usage</H2>
    <p>
      We use personal data to provide the services you request. To the extent we use your personal data to
      market to you, we will provide you the ability to opt out of such uses. We use your personal data to
      fulfil orders, enhance customer experience, resolve disputes, troubleshoot problems, inform you about
      offers, products, services and updates, detect and protect against error, fraud, and other criminal
      activity, enforce our terms and conditions, and conduct marketing research, analysis, and surveys.
    </p>

    <H2>Sharing</H2>
    <p>
      We may disclose personal data to third parties such as logistics/courier partners and payment
      processors where required to fulfil your order, comply with our legal obligations, or prevent, detect,
      and investigate fraudulent or illegal activities. We may disclose personal and sensitive personal data
      to government agencies or other authorised law enforcement agencies if required to do so by law, or
      in the good-faith belief that such disclosure is reasonably necessary to respond to subpoenas, court
      orders, or other legal process, or to protect the rights, property, or personal safety of our users or the
      general public.
    </p>

    <H2>Security Precautions</H2>
    <p>
      To protect your personal data from unauthorised access or disclosure, loss, or misuse, we adopt
      reasonable security practices and procedures. However, the transmission of information over the
      internet is not completely secure for reasons beyond our control, and certain inherent risks regarding
      the use of the Platform always remain. You are responsible for ensuring the protection of your
      login and password records.
    </p>

    <H2>Data Deletion and Retention</H2>
    <p>
      You have the option to delete your account by visiting your profile and settings on our Platform; this
      action will result in you losing all information related to your account. We retain your personal data
      for a period no longer than is required for the purpose for which it was collected, or as required
      under applicable law, except where we believe retention is necessary to prevent fraud or future abuse,
      or for other legitimate purposes.
    </p>

    <H2>Your Rights</H2>
    <p>You may access, rectify, and update your personal data directly through the functionality provided on the Platform, or by writing to us using the contact information below.</p>

    <H2>Consent</H2>
    <p>
      By visiting our Platform or providing your information, you consent to the collection, use, storage,
      disclosure, and processing of your information in accordance with this Privacy Policy. You may
      withdraw consent previously provided by writing to the Grievance Officer at the contact information
      below, with the subject line "Withdrawal of consent for processing personal data". Please note that
      withdrawal of consent will not be retrospective and will be in accordance with the Terms of Use, this
      Privacy Policy, and applicable laws.
    </p>

    <H2>Changes to this Privacy Policy</H2>
    <p>
      Please check our Privacy Policy periodically for changes. We may update this Privacy Policy to
      reflect changes to our information practices, and will notify you of significant changes as required
      under applicable law.
    </p>

    <H2>Grievance Officer</H2>
    <p>
      Name: Gubbala Chiru Mani Sai Raj<br />
      Designation: Grievance Officer<br />
      {BUSINESS_ADDRESS}<br />
      Phone: <a href={`tel:+${PHONE_NUMBER}`} className="text-[#556B2F] font-semibold hover:underline">{PHONE_DISPLAY}</a> (Monday – Friday, 9:00 AM – 6:00 PM)<br />
      Email: <a href="mailto:annapurnahomepickles@zohomail.in" className="text-[#556B2F] font-semibold hover:underline">annapurnahomepickles@zohomail.in</a>
    </p>
  </PolicyLayout>
  );
};

export const ShippingPolicyPage = () => {
  const policyContent = usePolicyContent();
  if (policyContent.shipping?.trim()) {
    return <PolicyLayout title="Shipping & Delivery Policy">{renderPolicyContent(policyContent.shipping)}</PolicyLayout>;
  }
  return (
  <PolicyLayout title="Shipping & Delivery Policy">
    <p>
      At <strong>Annapurna Home Pickles</strong>, we take pride in preparing and packaging our homemade
      pickles fresh for every order. Because our products are freshly prepared, orders are dispatched only
      after preparation and quality packaging are fully complete to ensure they reach you in perfect condition.
    </p>

    <H2>1. Shipping Methods & Costs</H2>
    <ul className="list-disc pl-5 space-y-2 mt-2">
      <li><strong>Our Carriers:</strong> Orders are shipped exclusively through registered domestic courier companies and/or India Post Speed Post.</li>
      <li><strong>Shipping Fees:</strong> Any applicable shipping or delivery costs are calculated based on your delivery address and order value. These costs are displayed clearly at checkout before you complete your payment.</li>
      <li><strong>Non-Refundable Policy:</strong> Shipping fees, where levied, are non-refundable once the order has been shipped.</li>
    </ul>

    <H2>2. Estimated Delivery Time</H2>
    <ul className="list-disc pl-5 space-y-2 mt-2">
      <li><strong>Dispatch Window:</strong> Orders are freshly prepared, securely packed, and shipped within <strong>1 to 3 business days</strong> from the date of the order and/or payment confirmation (or as per the delivery date agreed upon at the time of order confirmation).</li>
      <li>
        <strong>Delivery Timelines:</strong> Once handed over to the courier partner, actual delivery times depend on your location:
        <ul className="list-disc pl-5 mt-1 space-y-1">
          <li>🚚 <strong>Standard Delivery:</strong> 4 to 7 business days</li>
          <li>⚡ <strong>Express Delivery:</strong> 2 to 4 business days</li>
        </ul>
      </li>
      <li><strong>Delays & Liability:</strong> Actual transit times are subject to courier company or postal authority norms. Annapurna Home Pickles shall not be liable for delivery delays caused by the courier company or postal authority once the package has been handed over for shipping.</li>
    </ul>

    <H2>3. Order Tracking</H2>
    <ul className="list-disc pl-5 space-y-2 mt-2">
      <li><strong>Tracking Details:</strong> Once your order is dispatched, a courier tracking ID and/or tracking link will be added to your order. You can view this by navigating to{" "}
        <Link to="/my-orders" className="text-[#556B2F] font-semibold hover:underline">My Orders</Link> → Select the Order.</li>
      <li><strong>Status Updates:</strong> Real-time shipping status updates will also be sent automatically to the email ID you provided at checkout.</li>
      <li><strong>Delivery Address:</strong> All deliveries will be made strictly to the address provided by the buyer at the time of purchase. Please double-check your shipping address before finalising your order.</li>
    </ul>

    <H2>4. Shipping Coverage</H2>
    <ul className="list-disc pl-5 space-y-2 mt-2">
      <li><strong>Domestic Shipping:</strong> We currently ship all across India.</li>
      <li><strong>Remote Areas:</strong> Delivery to remote or rural pin codes may take a few additional days depending on our courier partner's specific regional coverage and transit schedules.</li>
    </ul>

    <H2>5. Contact Us</H2>
    <p>
      For any shipping, tracking, or delivery-related queries, please feel free to reach out to us at:<br />
      <strong>Annapurna Home Pickles</strong><br />
      Contact Person: CHIRUMANI SAI RAJA GUBBALA<br />
      2-283/3, Kadiyapusavaram, Kadiam, Rajahmundry Rural,<br />
      East Godavari District, Andhra Pradesh – 533101, India<br />
      Phone: <a href={`tel:+${PHONE_NUMBER}`} className="text-[#556B2F] font-semibold hover:underline">{PHONE_DISPLAY}</a><br />
      Email: <a href="mailto:annapurnahomepickles@zohomail.in" className="text-[#556B2F] font-semibold hover:underline">annapurnahomepickles@zohomail.in</a><br />
      Website: <a href="https://www.ahpickles.in/" className="text-[#556B2F] font-semibold hover:underline">https://www.ahpickles.in/</a>
    </p>
  </PolicyLayout>
  );
};

export const RefundPolicyPage = () => {
  const policyContent = usePolicyContent();
  if (policyContent.refund?.trim()) {
    return <PolicyLayout title="Refund Policy">{renderPolicyContent(policyContent.refund)}</PolicyLayout>;
  }
  return (
  <PolicyLayout title="Refund Policy">
    <p>
      This Refund Policy outlines how <strong>Annapurna Home Pickles</strong> handles refunds for
      orders placed through{" "}
      <a href="https://www.ahpickles.in/" className="text-[#556B2F] font-semibold hover:underline">https://www.ahpickles.in/</a>.
    </p>

    <H2>Eligibility for Refunds</H2>
    <p>Refunds are only granted under the following circumstances:</p>
    <ul className="list-disc pl-5 space-y-2 mt-2">
      <li>The product arrives <strong>damaged</strong> (broken or leaking jar/packaging).</li>
      <li>An <strong>incorrect item</strong> was sent (different flavour or size than ordered).</li>
      <li>The order was <strong>successfully cancelled</strong> before it was shipped.</li>
    </ul>

    <H2>Reporting Window</H2>
    <p>
      You must report any damage or incorrect items within <strong>24 hours</strong> of delivery.
      Requests raised after this window will not be eligible for a refund or replacement.
    </p>

    <H2>Proof Required</H2>
    <p>
      To process your request, please provide:
    </p>
    <ul className="list-disc pl-5 space-y-2 mt-2">
      <li>Your <strong>Order ID</strong> (found in your order confirmation or on the{" "}
        <Link to="/my-orders" className="text-[#556B2F] font-semibold hover:underline">My Orders</Link> page).</li>
      <li>Clear <strong>photographic or video evidence</strong> of the damage or incorrect product.</li>
    </ul>

    <H2>Approval Notice</H2>
    <p>
      Our team will review the details submitted and notify you via email or text message regarding
      the approval or rejection of your refund request.
    </p>

    <H2>Processing Time</H2>
    <p>
      Once approved, the refund will be processed by our team within <strong>2 to 3 business days</strong>.
    </p>

    <H2>Payment Method</H2>
    <p>
      The refund will be credited exclusively to the <strong>original payment method</strong> used
      during the purchase. We are unable to issue refunds to a different account or payment method.
    </p>

    <H2>Bank Timeline</H2>
    <p>
      Please allow up to <strong>7 business days</strong> for the credited amount to reflect in your
      bank account or card statement, depending on your financial institution.
    </p>

    <H2>Contact Us</H2>
    <p>
      To raise a refund request or for any related queries, please reach us at:<br />
      {BUSINESS_ADDRESS}<br />
      Phone: <a href={`tel:+${PHONE_NUMBER}`} className="text-[#556B2F] font-semibold hover:underline">{PHONE_DISPLAY}</a><br />
      Email: <a href="mailto:annapurnahomepickles@zohomail.in" className="text-[#556B2F] font-semibold hover:underline">annapurnahomepickles@zohomail.in</a>
    </p>
  </PolicyLayout>
  );
};

export const CancellationPolicyPage = () => {
  const policyContent = usePolicyContent();
  if (policyContent.cancellation?.trim()) {
    return <PolicyLayout title="Cancellation Policy">{renderPolicyContent(policyContent.cancellation)}</PolicyLayout>;
  }
  return (
  <PolicyLayout title="Cancellation Policy">
    <p>
      This Cancellation Policy describes the conditions under which orders placed with{" "}
      <strong>Annapurna Home Pickles</strong> through{" "}
      <a href="https://www.ahpickles.in/" className="text-[#556B2F] font-semibold hover:underline">https://www.ahpickles.in/</a>{" "}
      can be cancelled.
    </p>

    <H2>Time Window</H2>
    <p>
      Cancellation requests are accepted only within <strong>3 days</strong> of placing your order.
    </p>

    <H2>Shipping Status</H2>
    <p>
      Orders can only be cancelled <strong>before</strong> they have been shipped or handed over to
      our courier partner. Once an order has been dispatched, cancellation is no longer possible
      through our system.
    </p>

    <p className="bg-[#5A2D1D]/10 border border-[#5A2D1D]/20 rounded-sm px-4 py-3 text-sm">
      <strong>Important — Shipped Orders:</strong> If your order has already been shipped or is out for
      delivery, it cannot be cancelled. Your only option at this stage is to <strong>reject the
      package at your doorstep</strong> when the delivery partner arrives. Once rejected, please
      contact us so we can process the next steps.
    </p>

    <H2>How to Request a Cancellation</H2>
    <p>
      To cancel an eligible order, please contact us immediately with your <strong>Order Number</strong>{" "}
      (found on your order confirmation or on the{" "}
      <Link to="/my-orders" className="text-[#556B2F] font-semibold hover:underline">My Orders</Link>{" "}
      page):
    </p>
    <ul className="list-disc pl-5 space-y-2 mt-2">
      <li>
        WhatsApp: <a href={`https://wa.me/${WHATSAPP_NUMBER}`} className="text-[#556B2F] font-semibold hover:underline">{WA_DISPLAY}</a>
      </li>
      <li>
        Email: <a href="mailto:annapurnahomepickles@zohomail.in" className="text-[#556B2F] font-semibold hover:underline">annapurnahomepickles@zohomail.in</a>
      </li>
    </ul>

    <H2>Contact Us</H2>
    <p>
      For any cancellation queries, please reach us at:<br />
      {BUSINESS_ADDRESS}<br />
      Phone: <a href={`tel:+${PHONE_NUMBER}`} className="text-[#556B2F] font-semibold hover:underline">{PHONE_DISPLAY}</a><br />
      Email: <a href="mailto:annapurnahomepickles@zohomail.in" className="text-[#556B2F] font-semibold hover:underline">annapurnahomepickles@zohomail.in</a>
    </p>
  </PolicyLayout>
  );
};

export const ReturnPolicyPage = () => {
  const policyContent = usePolicyContent();
  if (policyContent.return?.trim()) {
    return <PolicyLayout title="Return Policy">{renderPolicyContent(policyContent.return)}</PolicyLayout>;
  }
  return (
  <PolicyLayout title="Return Policy">
    <p>
      This Return Policy describes the conditions under which products purchased from{" "}
      <strong>Annapurna Home Pickles</strong> through{" "}
      <a href="https://www.ahpickles.in/" className="text-[#556B2F] font-semibold hover:underline">https://www.ahpickles.in/</a>{" "}
      may be eligible for a refund.
    </p>

    <H2>Strict No-Return Policy</H2>
    <p>
      Due to the perishable nature of food products and for health and hygiene reasons, we do{" "}
      <strong>not accept returns</strong> on any of our pickles once they have been successfully
      delivered. We encourage you to review your order carefully before checking out. If you have
      questions about ingredients or spice levels, please reach out to us before purchasing.
    </p>

    <H2>Damaged or Incorrect Items</H2>
    <p>
      While we pack our pickles securely, we will gladly make things right if your order arrives
      under the following conditions:
    </p>
    <ul className="list-disc pl-5 space-y-2 mt-2">
      <li><strong>Damaged Products:</strong> The jar or packaging is broken, leaking, or visibly compromised upon delivery.</li>
      <li><strong>Incorrect Products:</strong> You received a different pickle flavour or size than what you ordered.</li>
    </ul>

    <p className="mt-4 font-semibold text-[#5A2D1D]">How to Claim a Refund:</p>
    <OL>
      <li>Notify us within <strong>24 hours</strong> of receiving your package.</li>
      <li>
        Email us at{" "}
        <a href="mailto:annapurnahomepickles@zohomail.in" className="text-[#556B2F] font-semibold hover:underline">annapurnahomepickles@zohomail.in</a>
        {" "}or message us on WhatsApp at{" "}
        <a href={`https://wa.me/${WHATSAPP_NUMBER}`} className="text-[#556B2F] font-semibold hover:underline">{WA_DISPLAY}</a>.
      </li>
      <li>Provide your <strong>Order Number</strong> and attach clear photographic or video evidence of the damage or incorrect item.</li>
    </OL>
    <p className="mt-3">
      Approved claims will be processed as a refund in accordance with our{" "}
      <Link to="/refund-policy" className="text-[#556B2F] font-semibold hover:underline">Refund Policy</Link>.
    </p>

    <H2>Contact Us</H2>
    <p>
      For any return or refund queries, please reach us at:<br />
      {BUSINESS_ADDRESS}<br />
      Phone: <a href={`tel:+${PHONE_NUMBER}`} className="text-[#556B2F] font-semibold hover:underline">{PHONE_DISPLAY}</a><br />
      Email: <a href="mailto:annapurnahomepickles@zohomail.in" className="text-[#556B2F] font-semibold hover:underline">annapurnahomepickles@zohomail.in</a>
    </p>
  </PolicyLayout>
  );
};
